import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { ThoughtChain } from "@ant-design/x";
import ObjectSelect from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { resolveUserRole } from "@shared/rbac";
import { useStoredUser } from "@shared/useStoredUser";
import { Alert, App as AntdApp, Button, Card, Empty, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AnalysisBubble } from "./AnalysisBubble";
import { useLatestAnalysis, useRunAnalysis } from "./api";
import { ConsumptionTable } from "./ConsumptionTable";
import { FindingsBadges } from "./FindingsBadges";
import type { AIAnalysis, AIHighlight } from "./types";
import { useAnalysisPolling } from "./useAnalysisPolling";

export const AIAnalysisWidget: React.FC = () => {
  const { message } = AntdApp.useApp();
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const user = useStoredUser();
  const role = resolveUserRole(user);
  const canRun = role !== "viewer";

  const regionId = selectedObject?.regionId;
  const objectId = selectedObject?.objectId;

  const objectName = useMemo(() => {
    if (!selectedObject) return "";
    const obj = objects.find(
      (o) => o.n_ob === selectedObject.objectId && o.syb_rnk === selectedObject.regionId,
    );
    return obj?.txt ?? `Объект ${selectedObject.objectId}`;
  }, [objects, selectedObject]);

  // Latest analysis for the selected object
  const latestQuery = useLatestAnalysis(regionId, objectId);

  // Polling state
  const [pollingId, setPollingId] = useState<string>();
  const pollingQuery = useAnalysisPolling(pollingId);

  // Determine active analysis: polling result takes precedence
  const analysis: AIAnalysis | undefined = pollingQuery.data ?? latestQuery.data ?? undefined;

  const runMutation = useRunAnalysis();

  const handleRun = useCallback(() => {
    if (!regionId || !objectId) return;
    runMutation.mutate(
      { region_id: regionId, object_id: objectId },
      {
        onSuccess: (res) => {
          if (res.cached) {
            message.info(`Актуальный анализ уже есть: ${objectName}`);
          } else {
            message.info(`Анализ запущен: ${objectName}`);
            setPollingId(res.id);
          }
        },
        onError: () => {
          message.error("Не удалось запустить анализ");
        },
      },
    );
  }, [regionId, objectId, objectName, runMutation, message.error, message.info]);

  // Stop polling on terminal status
  useEffect(() => {
    if (!pollingQuery.data) return;
    const st = pollingQuery.data.status;
    if (st === "success" || st === "partial") {
      message.success(`Анализ готов: ${objectName}`);
      setPollingId(undefined);
      latestQuery.refetch();
    } else if (st === "error") {
      message.error(pollingQuery.data.error_message ?? "Ошибка анализа");
      setPollingId(undefined);
    } else if (st === "empty") {
      setPollingId(undefined);
      latestQuery.refetch();
    }
  }, [
    pollingQuery.data?.status,
    message.error,
    message.success,
    objectName,
    pollingQuery.data,
    latestQuery.refetch,
  ]);

  // Reset polling when object changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on object change
  useEffect(() => {
    setPollingId(undefined);
  }, [regionId, objectId]);

  // Freshness check — disable refresh if <24h
  const isFresh = useMemo(() => {
    if (!analysis?.created_at) return false;
    return dayjs().diff(dayjs(analysis.created_at), "hour") < 24;
  }, [analysis?.created_at]);

  const hoursUntilRefresh = useMemo(() => {
    if (!analysis?.created_at) return 0;
    return Math.max(0, 24 - dayjs().diff(dayjs(analysis.created_at), "hour"));
  }, [analysis?.created_at]);

  // Parse highlights from JSON string
  const highlights: AIHighlight[] = useMemo(() => {
    if (!analysis?.highlights) return [];
    try {
      const parsed = JSON.parse(analysis.highlights);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [analysis?.highlights]);

  // Parse consumption table from input_data
  const consumptionData = useMemo(() => {
    if (!analysis?.input_data) return [];
    try {
      const parsed = JSON.parse(analysis.input_data);
      return Array.isArray(parsed.consumption_table) ? parsed.consumption_table : [];
    } catch {
      return [];
    }
  }, [analysis?.input_data]);

  const isPending = pollingQuery.isPolling || analysis?.status === "pending";

  const renderContent = () => {
    // Timeout — check before isPending because isPending includes status === "pending"
    if (pollingQuery.isTimeout) {
      return (
        <Alert
          type="warning"
          message="Анализ занимает больше времени"
          description="Обновите страницу позже"
          showIcon
        />
      );
    }

    // Pending — ThoughtChain progress
    if (isPending) {
      return (
        <ThoughtChain
          items={[
            {
              key: "collect",
              title: "Сбор данных",
              status: "success",
              icon: <CheckCircleOutlined />,
            },
            {
              key: "analyze",
              title: "Вычисление аномалий",
              status: "success",
              icon: <CheckCircleOutlined />,
            },
            {
              key: "ai",
              title: "Генерация отчёта ИИ",
              status: "loading",
              icon: <LoadingOutlined />,
            },
          ]}
        />
      );
    }

    if (!analysis) {
      return (
        <Empty
          image={<RobotOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />}
          description="Запустите ИИ-анализ, чтобы получить отчёт по аномалиям"
        >
          {canRun && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleRun}
              loading={runMutation.isPending}
            >
              ИИ-анализ
            </Button>
          )}
        </Empty>
      );
    }

    if (analysis.status === "error") {
      return (
        <Alert
          type="error"
          message="Ошибка анализа"
          description={analysis.error_message ?? "Неизвестная ошибка"}
          showIcon
        />
      );
    }

    if (analysis.status === "empty") {
      return <Empty description="Нет данных для анализа по объекту" />;
    }

    // success / partial
    return (
      <Space orientation="vertical" style={{ width: "100%" }} size="middle">
        {highlights.length > 0 && <FindingsBadges highlights={highlights} />}
        {analysis.summary && <AnalysisBubble content={analysis.summary} />}
        {consumptionData.length > 0 && <ConsumptionTable data={consumptionData} />}
        {analysis.status === "partial" && (
          <Alert
            type="info"
            message="Частичный анализ"
            description="ИИ-сервис был недоступен. Показаны только автоматические находки."
            showIcon
          />
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {analysis.created_at && dayjs(analysis.created_at).format("DD.MM.YYYY HH:mm")}
        </Typography.Text>
      </Space>
    );
  };

  return (
    <Card
      title={
        <Space>
          <ThunderboltOutlined />
          <span>ИИ-анализ объекта</span>
          <Tag color="blue">beta</Tag>
          <Tooltip title="Функция находится в стадии тестирования. ИИ может допускать неточности — проверяйте результаты.">
            <InfoCircleOutlined
              tabIndex={0}
              role="img"
              aria-label="Информация о beta"
              style={{ color: "#1677ff", cursor: "pointer" }}
            />
          </Tooltip>
        </Space>
      }
      extra={
        <Space>
          <ObjectSelect
            objects={objects}
            value={selectedObject}
            onChange={setSelectedObject}
            style={{ minWidth: 240 }}
          />
          {canRun && analysis && (
            <Tooltip title={isFresh ? `Доступно через ${hoursUntilRefresh} ч` : "Обновить анализ"}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRun}
                disabled={isFresh || runMutation.isPending}
                loading={runMutation.isPending}
              />
            </Tooltip>
          )}
          {canRun && !analysis && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleRun}
              loading={runMutation.isPending}
            >
              ИИ-анализ
            </Button>
          )}
        </Space>
      }
      loading={latestQuery.isLoading && !pollingId}
    >
      {renderContent()}
    </Card>
  );
};
